import { Button } from './ui/button';
import { useEffect, useState, useMemo } from 'react';
import { useOpportunityState } from '@/pages/AdvisorDashboardOpportunities';
import { SheetModal } from './SheetModal';
import { Loader2, Send } from 'lucide-react';
import { AdvisorWarningDialog } from './AdvisorWarningDialog';
import { postBatchClientEngagementEmailsRequest } from '@client/model/advisorOpportunitiesModel';
import { PersonalizedTextareaWithReset } from './PersonalizedTextareaWithReset';

export function SendOpportunityMessageModal({
  isOpen,
  onClose,
  advisor,
  defaultMessage,
  emailTemplate,
  emailTemplateProps,
  placeholderMessage,
  selectedClients,
  engagementType,
}: {
  isOpen: boolean;
  onClose: () => void;
  advisor: Advisor;
  defaultMessage: string | undefined;
  emailTemplateProps: any;
  emailTemplate: (props: any) => string;
  placeholderMessage?: string;
  selectedClients: Client[];
  engagementType: ClientEmailEngagement;
}) {
  const [message, setMessage] = useState<string | undefined>(defaultMessage);
  const { status, dispatch } = useOpportunityState(engagementType);
  const emailHtml = useMemo(
    () =>
      emailTemplate({
        ...emailTemplateProps,
        clientName:
          selectedClients.length === 1
            ? selectedClients[0].clientFirstName
            : '{{Client Name}}',
        personalizedMessage: message,
      }),
    [emailTemplate, emailTemplateProps, message, selectedClients],
  );

  // reset personalized message when modal is opened
  useEffect(() => {
    if (isOpen) {
      setMessage(message);
    }
  }, [isOpen, message]);

  const PreviewEmail = () => {
    return (
      <div className="my-4 space-y-4 rounded-lg border bg-[#f6f6f6] p-4">
        <div className="text-sm text-gray-500">
          Email Preview (links inactive):
        </div>
        <div
          dangerouslySetInnerHTML={{ __html: emailHtml }}
          className="max-h-[500px] overflow-auto text-gray-900"
        />
      </div>
    );
  };

  function handleSendReminder() {
    dispatch(
      postBatchClientEngagementEmailsRequest({
        clientIds: selectedClients.map(client => client.clientId),
        advisorId: advisor.advisorId,
        personalizedMessage: message,
        engagementType: engagementType,
      }),
    );
  }

  const isLoading = status === 'sendingEmails';

  return (
    <SheetModal
      open={isOpen}
      onClose={onClose}
      title="Review the email outreach message"
      subTitle="Edit the email message that will be sent to clients."
      primaryButton={
        <div className="flex items-center gap-4">
          {selectedClients.length > 0 && (
            <AdvisorWarningDialog
              message={`This may be considered marketing material and may require your firm's or carrier's compliance approval. By continuing, you confirm you have obtained or do not require such approval.`}
              onContinue={handleSendReminder}
            >
              <Button
                className="flex items-center gap-2 rounded-full bg-primary px-6 py-6 hover:bg-primary-hover disabled:opacity-50"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2
                    className="animate-spin"
                    size={16}
                  />
                ) : (
                  <Send size={16} />
                )}
                <span>
                  {`Deliver ${selectedClients.length} ${selectedClients.length === 1 ? 'Message' : 'Messages'}`}
                </span>
              </Button>
            </AdvisorWarningDialog>
          )}
        </div>
      }
    >
      <div>
        <div className="mt-4 flex flex-col gap-0.5">
          <label className="mb-1 ml-1 text-sm text-gray-900">
            Email body (optional)
          </label>
          <PersonalizedTextareaWithReset
            value={message}
            onChange={setMessage}
            placeholder={placeholderMessage ?? 'Add a personalized message'}
            minRows={2}
          />
        </div>

        <PreviewEmail />
      </div>
    </SheetModal>
  );
}
